import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import { styled } from "@mui/material/styles";
import {
  Grid,
  RadioGroup,
  CardActionArea,
  Typography,
  Box,
  Alert,
  Modal,
  Button, Stack,
} from "@mui/material";
// contexts
import useStore from "../../../contexts/JWTContext";
import { useTool } from "../../../contexts/ToolContext";
import usePlannerSettings from "../../../contexts/PlannerContext";
// hooks
import useAuth from "../../../hooks/useAuth";
// utils
import axios from "../../../utils/axios";
// components
import CustomModal from "../../../components/CustomModal";
import ModalConfirmClose from "../../../components/modals/ModalConfirmClose";
//
import { useTranslation } from "react-i18next";
import ClientForm from "../../planner/forms/ClientForm";
import BoxMask from "./BoxMask";
import Iconify from "../../Iconify";
import ToggleComplementiButton from "./ToggleComplementiButton";
import {useFurnitureStore} from "../../../contexts/SelectedVariantContext";

// ----------------------------------------------------------------------

const BoxStyle = styled(CardActionArea)(({ theme }) => ({
  height: 72,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.disabled,
  border: `solid 1px ${theme.palette.grey[500_12]}`,
  borderRadius: Number(theme.shape.borderRadius) * 1.25,
}));

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

// ----------------------------------------------------------------------

export default function SettingPlannerAction(props) {
  const { toolName } = useTool();
  const {
    quotation_id,
    rows,
    columns,
    line,
    dimension,
    visibleFacesVariation,
    hiddenFacesVariation,
    height,
    clientData,
    plan,
    setPlan,
    setQuotationId,
    setPlannerSettings,
    resetPlannerSettings,
  } = usePlannerSettings();
  const placedItems = useFurnitureStore(s => s.placedItems);
  const resetPlacedItems = useFurnitureStore(s => s.resetPlacedItems);
  const { getUserId } = useStore();
  const { role, isWebVersion } = useAuth();
  const [actions, setActions] = useState([
    { action: "save", icon: "save" },
    { action: "new", icon: "library-add" },
    { action: "empty", icon: "border-clear" },
  ]);
  const [guestActions, setGuestActions] = useState([
    { action: "new", icon: "library-add" },
    { action: "empty", icon: "border-clear" },
  ]);
  const [alertSuccess, setAlertSuccess] = useState(null);
  const [alertError, setAlertError] = useState(null);
  const [canSave, setCanSave] = useState(false);
  const [canDuplicate, setCanDuplicate] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [openEmptyModal, setOpenEmptyModal] = useState(false);
  const [modalConfirmOpenState, setModalConfirmOpenState] = useState(false);
  const [quotationSaved, setQuotationSaved] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleModalRefuse = () => {
    setOpenDeleteModal(false);
    setOpenSaveModal(false);
    setOpenDuplicateModal(false);
    setOpenEmptyModal(false);
  };

  const handleCanSave = (value) => {
    setCanSave(value);
  };

  const handleCanDuplicate = (value) => {
    setCanDuplicate(value);
  };

  const handleSettingsChange = (settings) => {
    setPlannerSettings(settings);
  };

  const handleSave = async () => {
    setOpenSaveModal(false);

    const data = {
      quotation_id,
      user_id: getUserId(), // user.user_id,
      rows,
      columns,
      line,
      dimension,
      visibleFacesVariation,
      hiddenFacesVariation,
      height,
      offer_number: clientData.offerNumber,
      date: clientData.date ? clientData.date?.toISOString?.()?.split?.("T")?.[0] : null,
      client_info: clientData.infos,
      notes: clientData.notes,
      client_references: clientData.references,
      plan: JSON.stringify(plan),
      temporary: "false",
      furnishings: JSON.stringify(placedItems)
    };

    try {
      const response = await axios.post("/quotations/save", data);

      setQuotationId(response.data.quotation.id);
      setAlertSuccess(t(response.data.message));
      setQuotationSaved(true);
      setTimeout(() => {
        setAlertSuccess(null);
        if (props.new === true) {
          navigate(`/dashboard/planner/${response.data.quotation.id}/modify`);
        }
      }, 2000);
    } catch (error) {
      console.error(error);

      setAlertError(t(error.message));
      setTimeout(() => {
        setAlertError(null);
      }, 3000);
    }
  };

  const handleDuplicate = async () => {
    setOpenDuplicateModal(false);

    const data = {
      user_id: getUserId(), // user.user_id,
      rows,
      columns,
      line,
      dimension,
      visibleFacesVariation,
      hiddenFacesVariation,
      height,
      offer_number: clientData.offerNumber,
      date: clientData.date ? clientData.date?.toISOString?.()?.split?.("T")?.[0] : null,
      client_info: clientData.infos,
      notes: clientData.notes,
      plan: JSON.stringify(plan),
      furnishings: JSON.stringify(placedItems)
    };

    try {
      const response = await axios.post("/quotations/save", data);

      setQuotationId(response.data.quotation.id);
      setAlertSuccess(t(response.data.message));
      setTimeout(() => {
        setAlertSuccess(null);
        navigate(`/dashboard/planner/${response.data.quotation.id}/modify`);
      }, 2000);
    } catch (error) {
      console.error(error);

      setAlertError(t(error.message));
      setTimeout(() => {
        setAlertError(null);
      }, 3000);
    }
  };

  const handleDelete = async () => {
    setOpenDeleteModal(false);

    try {
      const res = await axios.post(`/quotations/delete/${quotation_id}`);

      setQuotationId(null);
      setAlertSuccess(t(res.data.message));
      resetPlannerSettings();
      resetPlacedItems();
      setTimeout(() => {
        setAlertSuccess(null);
        navigate("/dashboard/planner");
      }, 2000);
    } catch (error) {
      console.error(error);

      setAlertError(t(error.message));
      setTimeout(() => {
        setAlertError(null);
      }, 3000);
    }
  };

  const handleEmpty = async () => {
    setOpenEmptyModal(false);

    var newPlan = [];

    var planRows = plan.length;
    var planColumns = plan[0].length;

    newPlan.length = planRows;

    for (let i = 0; i < newPlan.length; i++) {
      newPlan[i] = [];
      newPlan[i].length = planColumns;

      for (let j = 0; j < newPlan[i].length; j++) {
        newPlan[i][j] = {
          x: j,
          y: i,
          hovered: false,
          height: 0,
          brothers: [],
          slabs: [],
        };
      }
    }

    setPlan(newPlan);
  };

  const handleYesConfirmModal = () => {
    setModalConfirmOpenState(false);
    setOpenSaveModal(true);
  }

  const handleNoConfirmModal = () => {
    setModalConfirmOpenState(false);
    resetPlannerSettings();
    resetPlacedItems();
    navigate("/dashboard/planner");
  }

  const handleChange = (e, val) => {
    switch (val) {
      default:
        break;
      case "save":
        setOpenSaveModal(true);
        break;
      case "new":
        if ( !isWebVersion() && quotationSaved === false ) {
          setModalConfirmOpenState(true);
        } else {
          handleNoConfirmModal();
        }
        break;
      case "duplicate":
        setOpenDuplicateModal(true);
        break;
      case "delete":
        setOpenDeleteModal(true);
        break;
      case "empty":
        setOpenEmptyModal(true);
        break;
    }
  };

  const isSelected = (tool) => {
    if (toolName == tool) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (props.new !== true) {
      setActions([
        { action: "save", icon: "save" },
        { action: "new", icon: "note-add" },
        { action: "duplicate", icon: "library-add" },
        { action: "delete", icon: "delete" },
      ]);
    }
  }, [props.new]);

  const { complementiAttivi } = usePlannerSettings();

  return (
    <RadioGroup
      name="themePlannerTool"
      value="{toolName}"
      onChange={handleChange}
    >
      <CustomModal
        style={modalStyle}
        open={openDeleteModal}
        handleNo={handleModalRefuse}
        handleYes={handleDelete}
        title={t("dialogs.delete_configuration.title")}
        yesLable="Sì"
        noLabel="No"
      />
      {/* save modal */}
      <Modal open={openSaveModal} onClose={handleModalRefuse}>
        <Box sx={{ ...modalStyle }}>
          <Typography variant="h5" component="h2">
            {t("dialogs.save_configuration.title")}
          </Typography>
          <ClientForm
            canNext={handleCanSave}
            settings={{ clientData }}
            onSettingsChange={handleSettingsChange}
          />
          <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleModalRefuse}
            >
              {t("dialogs.buttons.undo")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!canSave}
              sx={{ ml: 2 }}
            >
              {t("dialogs.buttons.save")}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* duplicate modal */}
      <Modal open={openDuplicateModal} onClose={handleModalRefuse}>
        <Box sx={{ ...modalStyle }}>
          <Typography variant="h5" component="h2">
            {t("dialogs.duplicate_configuration.title")}
          </Typography>
          <ClientForm
            canNext={handleCanDuplicate}
            settings={{ clientData }}
            onSettingsChange={handleSettingsChange}
          />
          <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleModalRefuse}
            >
              {t("dialogs.buttons.undo")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDuplicate}
              disabled={!canDuplicate}
              sx={{ ml: 2 }}
            >
              {t("dialogs.buttons.duplicate")}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* empty modal */}
      <Modal open={openEmptyModal} onClose={handleModalRefuse}>
        <Box sx={{ ...modalStyle }}>
          <Typography variant="h5" component="h2">
            {t("dialogs.empty_planner.title")}
          </Typography>

          <Typography
            sx={{
              my: 2,
            }}
          >
            {t("dialogs.empty_planner.indication_1")}
          </Typography>

          <Box mt={2} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleModalRefuse}
            >
              {t("dialogs.buttons.undo")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEmpty}
              sx={{ ml: 2 }}
            >
              {t("dialogs.buttons.empty")}
            </Button>
          </Box>
        </Box>
      </Modal>
      {alertSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {alertSuccess}
        </Alert>
      )}
      {alertError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {alertError}
        </Alert>
      )}
      <Box sx={clientData.offerNumber || clientData.infos ? { mb: 2 } : null}>
        {role !== "GUEST" && clientData.offerNumber && (
          <Typography sx={{ fontSize: "15px" }}>
            {t("context.clientData.offer_number")}: {clientData.offerNumber}
          </Typography>
        )}
        {clientData.infos && (
          <Typography sx={{ fontSize: "15px" }}>
            {t("context.clientData.client_infos")}: {clientData.infos}
          </Typography>
        )}
      </Box>
      <Grid dir="ltr" container spacing={2.5}>
        {role === "GUEST"
          ? guestActions.map((action, index) => {
            return (
              <Grid key={action.action} item xs={6}>
                <BoxStyle
                  sx={{
                    flexDirection: "column",
                    ...(isSelected(action.action) && {
                      color: "primary.main",
                      boxShadow: (theme) => theme.customShadows.z20,
                    }),
                  }}
                >
                  <Box>
                    <Iconify
                      icon={`material-symbols:${action.icon}`}
                      width={28}
                      height={28}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      {t(`settings.drawer.actions.items.${action.action}`)}
                    </Typography>
                  </Box>
                  <BoxMask value={action.action} />
                </BoxStyle>
              </Grid>
            );
          })
          : actions.map((action, index) => {
            return (
              <Grid key={action.action} item xs={6}>
                <BoxStyle
                  sx={{
                    flexDirection: "column",
                    ...(isSelected(action.action) && {
                      color: "primary.main",
                      boxShadow: (theme) => theme.customShadows.z20,
                    }),
                  }}
                >
                  <Box>
                    <Iconify
                      icon={`material-symbols:${action.icon}`}
                      width={28}
                      height={28}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2">
                      {t(`settings.drawer.actions.items.${action.action}`)}
                    </Typography>
                  </Box>
                  <BoxMask value={action.action} />
                </BoxStyle>
              </Grid>
            );
          })
        }
      </Grid>

      {/*{!complementiAttivi && (*/}
      {/*    <Stack spacing={2}>*/}
      {/*      <ToggleComplementiButton />*/}
      {/*    </Stack>*/}
      {/*)}*/}

      <ModalConfirmClose openState={modalConfirmOpenState} handleYesModal={handleYesConfirmModal} handleNoModal={handleNoConfirmModal}></ModalConfirmClose>
    </RadioGroup>
  );
}
